/* eslint-disable max-lines */
/* eslint-disable require-jsdoc */
"use client";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import IconButtonComponent from "../../../components/atoms/iconButton";
import { constants } from "../../../constants/pageConstants/pdpConstants";
import { requestAddToCart, requestToCartNotification, setIsCartVisible } from "../../../store/slices/cartSlice";
import { requestGetDeliveryTimeLeft, setBuyNowOrProceedToPayClick } from "../../../store/slices/pdpSlice";
import isEmpty from "lodash.isempty";
import { usePathname, useRouter } from "next/navigation";
import { BUY_NOW, GO_TO_CART, PDP } from "../../../constants/common/common";
import CartNotificationPopOver from "../../../components/molecules/cartBody/cartNotificationPopOver";
import Typography from "../../../components/atoms/typography";
import { plpPageConstants } from "../../../constants/pageConstants/plpConstants";
import ButtonComponent from "../../../components/atoms/button";
import { getOutOfStockRedirectionUrlsAPI } from "../../../api/pdpApi";
import { checkIsInternationalProduct } from "../../../utils/pdp/nonDeliverable";
import { validateAllRequiredFieldsToAddToCart } from "../../../utils/pdp/common";
import { checkPersonalizationValidation } from "../../../utils/pdp/personalization";
import getFnpSalesChannelInfo from "../../../utils/fnpChannelsInfo";

const CountDownTimer = dynamic(
  () => import("../../../components/molecules/countdownTimer").then((mod) => mod.CountDownTimer),
  {
    ssr: false,
  },
);
import { cleverTapCommonEvents, cleverTapCommonWords, validationFor } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { debounce, pageName, redirectToCheckoutOrLogin } from "../../../utils/common";
import { resetGetDeliveryDateAndGeoPriceAction } from "../../../store/slices/common/shippingDateAndSlotSelection";
import { setMandateErrorAction } from "../../../store/slices/locationLock";

/**
 * Component that displays a section with "Add To Cart" and "Buy Now" buttons.
 * The section becomes fixed when it is in view and relative when it is out of view.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.currentItem - The current product item.
 * @param {Object} props.isMobile - is mobile check.
 * @returns {JSX.Element} The rendered BuyNowSection component.
 */
const BuyNowSection = ({
  currentItem,
  isNotServiceable,
  isMobile,
  isOutOfStock,
  onButtonClick,
  commonCleverTapData,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();
  const isInternational = checkIsInternationalProduct(currentItem?.primaryCategoryId);
  const [productCategory, setProductCategory] = useState("Gift");
  const [outOfStockUrls, setOutOfStockUrls] = useState(null);
  const outOfStockRedirectionUrl = isMobile ? outOfStockUrls?.mSite : outOfStockUrls?.dSite;
  const userPersonalization = useSelector((state) => state.personalization.userPersonalization);
  const deliveryTimeLeft = useSelector((state) => state.pdp.deliveryTimeLeft);

  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");
  const previousURL = typeof document !== "undefined" && document.referrer.length ? new URL(document.referrer) : "";
  const url = typeof document !== "undefined" ? new URL(window.location.href) : null;
  const occasionTag = url?.searchParams?.get("OCCASION_TAGS") || "";
  const isCartVisible = useSelector((state) => state?.cart?.isCartVisible);

  const {
    deliveryDetailsData,
    userSelectedDelivery,
    pincode,
    address,
    isSameItemInCart,
    checkLoginStatus,
    catalogId,
    selectedIntLocation,
    selectedCurrency,
    updatedDeliveryDatesAndGeoPrice,
    fullProductDetails,
    multiProductDateRange,
  } = useSelector(
    (state) => ({
      deliveryDetailsData: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      userSelectedDelivery: state?.pdp?.userSelectedDelivery,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      address: state?.locationLock?.selectedDomesticLocation?.address,
      isSameItemInCart: state.cart?.isSameItemInCart,
      checkLoginStatus: state.fus.loginDetail?.loggedin,
      catalogId: state.appConfig.catalogId,
      selectedIntLocation: state?.locationLock.selectedIntLocation,
      selectedCurrency: state?.currencyList?.userSelectedCurrency,
      updatedDeliveryDatesAndGeoPrice: state?.shippingDateAndSlotSelection?.updatedDeliveryDatesAndGeoPrice,
      fullProductDetails: state?.pdp?.fullProductDetails,
      multiProductDateRange: state?.shippingDateAndSlotSelection?.multiProductDateRange,
    }),
    shallowEqual,
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    if (document.referrer.length > 0) {
      const refUrlString = new URL(document.referrer);
      setProductCategory(refUrlString.pathname !== url.pathname ? refUrlString.pathname : "Gift");
    }
    dispatch(requestGetDeliveryTimeLeft(currentItem?.productId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCartVisible) {
      dispatch(
        requestToCartNotification({
          isMobile,
          screen: PDP,
          productId: { variantProductId: currentItem?.productId, mainProductId: fullProductDetails?.productId },
        }),
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (isOutOfStock) {
        const response = await getOutOfStockRedirectionUrlsAPI();
        setOutOfStockUrls(response);

        const cleverTapData = {
          page_name: pageName(isHome, isPLP, pathname, currentItem?.urlIdentifier),
          pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
          validation_text: constants.OUT_OF_STOCK_PDP,
          validation_for: validationFor.OUT_OF_STOCK,
        };

        trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOutOfStock]);

  /**
   * Handles adding an item to the cart after validation.
   * @returns {void}
   */
  const onAddToCartClick = debounce((eventType = GA4_EVENTS?.ADD_TO_CART) => {
    const personalizationValidation = checkPersonalizationValidation(
      currentItem?.personalizedInfo,
      userPersonalization,
    );

    // Destructure FNP sales channel information with defaults
    const { fnpSalesChannelEnumId = "", utmCampaign = "" } = getFnpSalesChannelInfo(isMobile) || {};

    // Validate required fields before proceeding
    const isValidationFailed = validateAllRequiredFieldsToAddToCart(
      isInternational ? selectedIntLocation?.pincode : pincode,
      isEmpty(deliveryDetailsData),
      personalizationValidation,
      dispatch,
    );
    if (isValidationFailed) {
      dispatch(setMandateErrorAction(true));
      return;
    }

    const url = new URL(window?.location?.href);
    const basePayload = {
      price: isInternational
        ? updatedDeliveryDatesAndGeoPrice?.price || currentItem?.productPrice?.price
        : currentItem?.productPrice?.price,
      quantity: 1,
      productType: currentItem?.primaryCategoryId,
      isbuynow: "N",
      fnpSalesChannelEnumId,
      product_id: fullProductDetails?.productId,
      add_category_id: productCategory,
      add_product_id: fullProductDetails?.isVirtual === "Y" ? currentItem?.productId : fullProductDetails?.productId,
      pinCode: isInternational ? selectedIntLocation?.pincode : pincode,
      selectedCurrency: selectedCurrency?.currency?.id,
      destlookup: isInternational ? "" : address,
      deliveryLocation: isInternational ? selectedIntLocation?.location : address,
      itemDesiredDeliveryDate: deliveryDetailsData?.selectedDate,
      matchedPincode: isInternational ? " " : pincode,
      shippingCost: deliveryDetailsData?.timeSlots?.timeSlots?.shippingPrice,
      shippingMethodId: deliveryDetailsData?.currentShippingMethod?.shippingMethodId,
      shippingMethodName: deliveryDetailsData?.currentShippingMethod?.shippingMethodName,
      timeSlotId: deliveryDetailsData?.timeSlots?.timeSlots?.timeSlotId,
      countryGeoId: currentItem?.countryGeoId,
      occasion: url?.searchParams?.get("OCCASION_TAGS") || "",
      vendorId: "",
      productSequenceNumber: "",
      itemDesiredDeliveryDatesList: multiProductDateRange?.join(","),
      destlookupintl: isInternational ? selectedIntLocation?.location : "",
      selectedCity: isInternational ? selectedIntLocation?.location : "",
      userSelectedDelivery: userSelectedDelivery?.deliveryType || "",
      dynamicStartTime: deliveryDetailsData?.timeSlots?.timeSlots?.startTime,
      dynamicEndTime: deliveryDetailsData?.timeSlots?.timeSlots?.endTime,
    };

    // Add personalization options if applicable
    if (currentItem.personalizedInfo?.personalizedProduct && userPersonalization.length > 0) {
      basePayload.personalizedOptions = JSON.stringify(userPersonalization);
    }

    // Add UTM campaign if available
    if (utmCampaign) {
      basePayload.ptc = utmCampaign;
    }
    const event = eventType === GA4_EVENTS?.BUY_NOW ? GA4_EVENTS?.BUY_NOW : GA4_EVENTS?.ADD_TO_CART;
    onButtonClick(event);
    dispatch(requestAddToCart(basePayload));
    dispatch(resetGetDeliveryDateAndGeoPriceAction());
    const eventName =
      event === GA4_EVENTS?.ADD_TO_CART ? cleverTapCommonEvents.addToCartClicked : cleverTapCommonEvents.buyNowClicked;
    const eventData = {
      ...commonCleverTapData,
      product_rating: currentItem?.productReviewsAndRatings?.ratingValue,
      product_review_count: currentItem?.productReviewsAndRatings?.totalReview,
    };
    trackEvent(eventName, eventData, ["clevertap"]);
    const thankyouCleverTapData = JSON.parse(window.localStorage.getItem("thankYouPageTrackingData")) || {
      productDetails: [],
      couponCode: "",
      senderEmail: "",
    };

    thankyouCleverTapData.productDetails.push({
      productId: fullProductDetails?.isVirtual === "Y" ? currentItem?.productId : fullProductDetails?.productId,
      previousURL,
      shippingSlots: deliveryDetailsData?.timeSlots?.timeSlots,
      deliveryDates: deliveryDetailsData?.selectedDate,
      productUrl: window.location.pathname,
      occasionTag,
    });

    window.localStorage.setItem("thankYouPageTrackingData", JSON.stringify(thankyouCleverTapData));
  }, 100);

  /**
   * Redirects the user to the checkout or login page based on their login status.
   *
   * @function handleCheckoutRedirect
   * @returns {void} This function does not return a value.
   */
  const handleCheckoutRedirect = () => {
    if (!checkLoginStatus) {
      dispatch(setBuyNowOrProceedToPayClick(GO_TO_CART));
    }
    redirectToCheckoutOrLogin({
      checkLoginStatus,
      isMobile,
      dispatcher: dispatch,
    });
  };

  /**
   * Handles the 'Buy Now' button click.
   * @returns {void}
   */
  const onBuyNowClick = () => {
    if (isSameItemInCart) {
      handleCheckoutRedirect();
    } else {
      dispatch(setIsCartVisible(false));
      onAddToCartClick(GA4_EVENTS?.BUY_NOW);
      dispatch(setBuyNowOrProceedToPayClick(BUY_NOW));
    }
  };

  /**
   * Redirect to Gift pages when the current product is not available.
   */
  const onViewAvailableGiftsClick = () => {
    const refUrlString = document?.referrer?.length && new URL(document?.referrer);
    const redirectToPlp = refUrlString?.href;
    const cityPincode = isInternational ? selectedIntLocation?.pincode : pincode;
    if (redirectToPlp?.includes("-lp")) {
      if (redirectToPlp?.includes("?")) {
        const redirectToPlpArr = redirectToPlp.split("?");
        return router.push(`${redirectToPlpArr[0]}?promo=vag_pdp_sameplp_${cityPincode}`);
      }
      return router.push(`${redirectToPlp}?promo=vag_pdp_sameplp_${cityPincode}`);
    }
    return router.push(`${constants.REDIRECT_TO_GIFTS_LP}?promo=vag_pdp_allgifts_${cityPincode}`);
  };

  /**
   * Handles redirection logic when an item is out of stock.
   * Redirects back to the previous page if available or to specific pages based on conditions.
   * @returns {void}
   */
  const onOutOfStockRedirectHandler = () => {
    const eventData = {
      page_name: currentItem?.urlIdentifier,
      page_type: cleverTapCommonWords.PDP,
      validation_text: validationFor.PRODUCT_IS_OUT_OF_STOCK,
      validation_for: cleverTapCommonWords.locationField,
      pid: currentItem?.productId,
      product_name: currentItem?.productName,
      sku_id: currentItem?.skuCode,
      pincode: isInternational ? selectedIntLocation?.pincode : pincode,
      system_pincode: isInternational ? selectedIntLocation?.pincode : pincode,
      area: isInternational ? selectedIntLocation?.location : address,
    };
    trackEvent(cleverTapCommonEvents.validationViewed, eventData, ["clevertap"]);
    const previousPage = document.referrer.length && new URL(document.referrer);
    if (previousPage?.pathname?.includes("lp")) {
      window.location.href = previousPage.href;
    } else if (isInternational) {
      const homeURL = catalogId === "singapore" ? `/${catalogId}/rakhi` : `/${catalogId}/gifts`;
      router.push(homeURL);
    } else {
      router.push(outOfStockRedirectionUrl);
    }
  };

  const onKeyDownHandler = (e) => {
    if (e.key === "Enter") {
      onOutOfStockRedirectHandler();
    }
  };

  const renderOutOfStockElement = () => {
    return (
      <>
        <Typography
          variant="p"
          Tag="p"
          classes={`text-red-100 text-12 font-400 text-center pt-10 pb-13 bg-[#FDF4F2] ${!isMobile ? "mb-8 rounded-lg font-600" : ""}`}
        >
          {constants.OUT_OF_STOCK_PDP}
        </Typography>
        <ButtonComponent
          size="medium"
          classes={`text-base px-16 ${isMobile ? "mt-12 mb-16 mx-16" : ""}`}
          buttonVariant="filled"
          buttonTextSize="large"
          onClick={onOutOfStockRedirectHandler}
          label={constants.VIEW_AVAILABLE_GIFTS}
          onKeyDown={onKeyDownHandler}
        />
      </>
    );
  };

  const renderNotServiceableElement = () => (
    <>
      <Typography
        variant="p"
        Tag="p"
        classes={`text-fnp-500 text-12 font-400 text-center pt-10 pb-13 ${isMobile ? "bg-blue-500" : "bg-white-900"}`}
      >
        {plpPageConstants.SEE_ALL_AVAILABLE_PRODUCTS}{" "}
        <strong>{!isInternational ? pincode : selectedIntLocation?.location}</strong>
      </Typography>
      <ButtonComponent
        size="medium"
        classes={`text-base px-16 ${isMobile ? "my-12 mx-16" : ""}`}
        buttonVariant="filled"
        buttonTextSize="large"
        onClick={onViewAvailableGiftsClick}
        label={constants.VIEW_AVAILABLE_GIFTS}
      />
    </>
  );

  const getStatusStyles = () => {
    let styles = "";
    if ((isNotServiceable && isMobile) || isOutOfStock) {
      styles = "flex-col";
    } else if (isNotServiceable && !isMobile) {
      styles = "flex-col-reverse";
    } else if (!(isNotServiceable && isMobile)) {
      styles = "gap-8 px-8 pt-12 pb-16";
    }
    return styles;
  };

  return (
    <>
      {isMobile ? <CartNotificationPopOver handleCheckoutRedirect={handleCheckoutRedirect} /> : <></>}
      <div className="fixed bottom-0 left-0 z-2 flex w-full  flex-col-reverse justify-center bg-white-900 md:sticky md:z-0 md:flex-col">
        <div className={`flex w-full justify-between ${getStatusStyles()} md:px-0`}>
          {!isOutOfStock ? (
            !isNotServiceable ? (
              <>
                <IconButtonComponent
                  labelColor="text-base w-auto font-600 pl-4"
                  label={constants.ADD_TO_CART}
                  altText={`${constants.ADD_TO_CART} icon`}
                  buttonVariant="iconWithTextOutlined"
                  iconSrc={constants.ADD_TO_CART_ICON}
                  classes="border-green-100 bg-white-900 hover:bg-fnp-50 ease-in-out duration-300 font-600 text-green-100 md:py-8 lg:py-14 py-13 flex items-center rounded-lg w-full"
                  imgWidth={24}
                  imgHeight={24}
                  onClick={onAddToCartClick}
                  isPriority={false}
                />
                <IconButtonComponent
                  labelColor="text-base w-auto font-600 pl-4"
                  label={isSameItemInCart ? constants.GO_TO_CART : constants.BUY_NOW}
                  altText={`${isSameItemInCart ? constants.GO_TO_CART : constants.BUY_NOW} icon`}
                  buttonVariant="iconWithoutTextFilled"
                  iconSrc={constants.BUY_NOW_ICON}
                  classes="flex font-bold items-center py-14 !px-30 rounded-lg md:py-10 lg:py-14 text-white-900 w-full ease-in-out duration-300 hover:bg-opacity-80"
                  imgWidth={24}
                  imgHeight={24}
                  onClick={onBuyNowClick}
                  isPriority={false}
                />
              </>
            ) : (
              renderNotServiceableElement()
            )
          ) : (
            renderOutOfStockElement()
          )}
        </div>
        {!isOutOfStock && !isNotServiceable && deliveryTimeLeft?.isDeliveryTimeAvailable && (
          <CountDownTimer deliveryTimeLeft={deliveryTimeLeft} />
        )}
      </div>
    </>
  );
};

BuyNowSection.propTypes = {
  /** The current product item. */
  currentItem: PropTypes.shape({
    productPrice: PropTypes.shape({
      price: PropTypes.number,
    }),
    productName: PropTypes.string,
    skuCode: PropTypes.string,
    primaryCategoryId: PropTypes.string,
    countryGeoId: PropTypes.string,
    productId: PropTypes.string,
    personalizedInfo: PropTypes.shape({
      personalizedProduct: PropTypes.bool,
    }),
    productReviewsAndRatings: PropTypes.shape({
      ratingValue: PropTypes.number,
      totalReview: PropTypes.number,
    }),
    urlIdentifier: PropTypes.string,
  }).isRequired,
  isNotServiceable: PropTypes.bool,
  isMobile: PropTypes.bool,
  isOutOfStock: PropTypes.bool,
  onButtonClick: PropTypes.func,
  commonCleverTapData: PropTypes.object,
};

export default BuyNowSection;
